import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Tabs, Page, Spinner, Text, LegacyStack } from "@shopify/polaris";
import styled from "styled-components";

import { useSmartrrVendorSelector } from "@vendor-app/app/_state/typedVendorReduxHooks";
import { useSmartrrFlags } from "@smartrr/shared/LaunchDarkly";

import { NextOrderDate } from "./NextOrderDate";
import { SellingPlan } from "./SellingPlan";
import PriceChanges from "./PriceChanges";
import { BulkUpdateFileUploadStoreAccess } from "@vendor-app/app/_sharedComponents/FileImports/stores/BulkUpdateFileUpload";

enum TabName {
  NEXT_ORDER_DATE = "NEXT_ORDER_DATE",
  PRICE_CHANGES = "PRICE_CHANGES",
  SELLING_PLAN = "SELLING_PLAN",
}

const TABS_CONFIG: Record<TabName, { content: string }> = {
  [TabName.NEXT_ORDER_DATE]: { content: "Next Order Date" },
  [TabName.PRICE_CHANGES]: { content: "Price Changes" },
  [TabName.SELLING_PLAN]: { content: "Selling Plan" },
};

const PageWrapper = styled.div`
  > .Polaris-Page {
    max-width: max(64rem, 75%);
  }
`;

export const AdminBulkUpdatesRoute = () => {
  const { bulkUpdatesSellingPlanTab, variantPriceChangeRecording } = useSmartrrFlags();
  const authIsInitializing = useSmartrrVendorSelector(state => state.auth.isLoading);
  const user = useSmartrrVendorSelector(state => state.auth.user);
  const uploadActions = BulkUpdateFileUploadStoreAccess.useActions();

  const [selectedTabName, setSelectedTabName] = useState(TabName.NEXT_ORDER_DATE);

  useEffect(() => {
    if (
      variantPriceChangeRecording &&
      new URLSearchParams(window.location.search).get("tab") === "price-changes"
    ) {
      setSelectedTabName(TabName.PRICE_CHANGES);
    }
  }, [variantPriceChangeRecording]);

  const availableTabs = useMemo(() => {
    const result: TabName[] = [TabName.NEXT_ORDER_DATE];

    if (variantPriceChangeRecording) {
      result.push(TabName.PRICE_CHANGES);
    }

    if (bulkUpdatesSellingPlanTab) {
      result.push(TabName.SELLING_PLAN);
    }

    return result;
  }, [variantPriceChangeRecording, bulkUpdatesSellingPlanTab]);

  const tabs = useMemo(
    () =>
      availableTabs.map(tabName => ({
        id: tabName,
        content: TABS_CONFIG[tabName].content,
      })),
    [availableTabs]
  );

  useEffect(() => {
    uploadActions.setUser(user);
  }, [user, uploadActions]);

  const handleTabChange = useCallback(
    (selectedTabIndex: number) => {
      setSelectedTabName(availableTabs[selectedTabIndex]);
    },
    [availableTabs]
  );

  const tabsInner = useMemo(() => {
    if (authIsInitializing) {
      return <Spinner />;
    }

    return (() => {
      switch (selectedTabName) {
        case TabName.NEXT_ORDER_DATE: {
          return <NextOrderDate />;
        }
        case TabName.PRICE_CHANGES: {
          return <PriceChanges />;
        }
        case TabName.SELLING_PLAN: {
          return <SellingPlan />;
        }
        default: {
          return null;
        }
      }
    })();
  }, [selectedTabName, authIsInitializing]);

  const LegacyStackContainer = styled.div`
    & > .Polaris-LegacyStack > .Polaris-LegacyStack__Item:first-child {
      margin-top: 40px;
    }
  `;

  const selectedTabIndex = availableTabs.indexOf(selectedTabName);

  return (
    <PageWrapper>
      <Page>
        <Tabs tabs={tabs} selected={selectedTabIndex} onSelect={handleTabChange} fitted>
          <LegacyStackContainer>
            <LegacyStack vertical spacing="loose">
              <Text variant="headingLg" as="h2" fontWeight="semibold">
                Bulk Updates
              </Text>
              {tabsInner}
            </LegacyStack>
          </LegacyStackContainer>
        </Tabs>
      </Page>
    </PageWrapper>
  );
};
